import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ErrorMessage } from "../../../utils/commonUtils";
import { SUCCESS } from "../../../utils/constants";
import { ClientProfileActions, SettingsActions } from "../../api/RequestUtil";
import AlertService from "../../common/Alert/alertService";
import PaymentSuccess from "./PaymentSuccess";
import theme from "../../../theme";
import { setIsCardDetailsFilled, setPlanDetails } from "../../../redux/features/userSlice";
import { PAYMENT_STATUS_ACTIVE, PAYMENT_STATUS_PAST_DUE } from "../../../constants/constant";
import SecureFields from "../../common/SecureFields";
import CustomDialog from "../../common/CustomDialog";

const UpdateBilling = ({
  open,
  handleClose,
  title = "Update Billing",
  handleSaveTitle = "Update",
  isPayNow,
  clientId,
  subscriptionId,
  updateBillingMessage = "",
  setChangePlan = () => {},
  showChangePlanAfterUpdateBilling = false,
}) => {
  const dispatch = useDispatch();
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [cardToken, setCardToken] = useState(null);
  const cardTokenRef = useRef(null);
  const [fieldsValid, setFieldsValid] = useState(false);
  const fieldsValidRef = useRef(false);
  // Use ref for pendingSubmit instead of state
  const pendingSubmitRef = useRef(false);
  const {
    accountOwnerDetails: { address, firstName, lastName, companyName, email, phone },
  } = useSelector((state) => state.settings);
  const { isRiskFreeTier, planRemainingGraceDays } = useSelector((state) => state.user);
  const [country, setCountry] = useState("");
  const [loader, setLoader] = useState(false);
  const { success, error } = AlertService();

  // Removed react-hook-form usage for Secure Fields integration

  useEffect(() => {
    if (open && address) {
      const addressParts = address.split(",");
      const extractedCountry = addressParts[addressParts.length - 1].trim();
      setCountry(extractedCountry);
    }
  }, [open, address]);

  const submitToMktoForms = () => {
    if (country === "New Zealand" || country === "Australia") {
      const myForm = window.MktoForms2.allForms()[0];
      myForm.addHiddenFields({
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        Phone: phone,
        Company: companyName,
        eWAY_Branch__c: country,
      });
      window.MktoForms2.whenReady((myForm2) => {
        myForm2.submit();
        myForm2.onSuccess(() => false);
      });
    }
  };

  // Handle token from SecureFields
  const processPayment = async (card_token) => {
    try {
      setLoader(true);
      const cardData = { card_token };
      const response = isPayNow
        ? await ClientProfileActions.payNowForSubscription(clientId, subscriptionId, cardData)
        : await SettingsActions.updateBilling(cardData); // Use PUT for PCI DSS compliance

      submitToMktoForms();

      if (response.data.status === SUCCESS) {
        success(response.data?.message);
        !showChangePlanAfterUpdateBilling && setPaymentSuccess(true);
        showChangePlanAfterUpdateBilling && setChangePlan(true);

        if (isRiskFreeTier) {
          dispatch(
            setPlanDetails({
              status: PAYMENT_STATUS_PAST_DUE,
              planRemainingGraceDays,
            })
          );
          dispatch(setIsCardDetailsFilled(true));
        } else {
          dispatch(
            setPlanDetails({
              status: PAYMENT_STATUS_ACTIVE,
              planRemainingGraceDays: null,
            })
          );
        }
      }
    } catch (err) {
      error(ErrorMessage(err.data));
    } finally {
      setLoader(false);
      handleClose(); // Always close without reload after payment
    }
  };

  // SecureFields token handler (no longer used for direct callback)
  const handleSecureFieldsToken = (token) => {
    console.log("Received token from SecureFields:", token);
    setCardToken(token);
    cardTokenRef.current = token;
    const valid = window.isSecureFieldsValid && window.isSecureFieldsValid();
    setFieldsValid(valid);
    fieldsValidRef.current = valid;
    // If pendingSubmit is true and fields are valid, process payment and reset pendingSubmit
    if (pendingSubmitRef.current && valid && token) {
      pendingSubmitRef.current = false;
      setLoader(true);
      processPayment(token)
        .catch(() => {
          error("Error processing payment. Please try again.");
        })
        .finally(() => {
          setLoader(false);
          handleClose();
        });
      return; // Prevent retry loop from double-submitting
    }
  };

  const handleCloseModal = () => {
    handleClose();
    if (!paymentSuccess) {
      window.location.reload();
    }
  };

  // Handler for update button click
  const handleUpdateClick = async () => {
    // Blur the currently focused element to trigger Secure Fields validation
    const activeElement = document.activeElement;
    if (activeElement) {
      activeElement.blur();
    }
    // Retry logic: up to 3 times, 100ms apart
    pendingSubmitRef.current = true;
    let attempts = 0;
    const maxAttempts = 3;
    const retryDelay = 100;
    const trySubmit = () => {
      // Only run if pendingSubmit is still true (callback may have already handled it)
      if (pendingSubmitRef.current && fieldsValidRef.current && cardTokenRef.current) {
        pendingSubmitRef.current = false;
        setLoader(true);
        processPayment(cardTokenRef.current)
          .catch(() => {
            error("Error processing payment. Please try again.");
          })
          .finally(() => {
            setLoader(false);
            handleClose();
          });
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(trySubmit, retryDelay);
      } else if (pendingSubmitRef.current) {
        pendingSubmitRef.current = false;
        error("Card details are incomplete or invalid. Please check your input.");
      }
    };
    setTimeout(trySubmit, retryDelay);
  };

  return (
    <>
      <CustomDialog
        title={title}
        open={open}
        handleClose={handleCloseModal}
        handleSaveTitle={handleSaveTitle}
        loader={loader}
        onCloseButtonClick={handleCloseModal}
        handleSave={handleUpdateClick} // Use this for update button
        handleSaveDisabled={!fieldsValid || !cardToken}
      >
        {updateBillingMessage && (
          <Typography color={theme.palette.red.dark} fontWeight="700" marginBottom="20px">
            {updateBillingMessage}
          </Typography>
        )}
        <Box marginTop={1}>
          {/* SecureFields replaces PaymentForm for card entry */}
          <SecureFields
            apiKey={process.env.REACT_APP_EWAY_PUBLIC_API_KEY}
            onToken={handleSecureFieldsToken}
            open={open}
            sx={{ margin: "0 auto", width: "300px", marginBottom: "40px" }}
          />
        </Box>
      </CustomDialog>
      {paymentSuccess && (
        <PaymentSuccess
          open={paymentSuccess}
          handleClose={() => {
            setPaymentSuccess(false);
            window.location.reload();
          }}
        />
      )}
    </>
  );
};

UpdateBilling.propTypes = {
  open: PropTypes.bool,
  isPayNow: PropTypes.bool,
  handleClose: PropTypes.func,
  handleCloseButtonClick: PropTypes.func,
  title: PropTypes.string,
  handleSaveTitle: PropTypes.string,
  clientId: PropTypes.string,
  subscriptionId: PropTypes.string,
  refetchData: PropTypes.func,
  updateBillingMessage: PropTypes.string,
  setChangePlan: PropTypes.func,
  showChangePlanAfterUpdateBilling: PropTypes.bool,
};

export default UpdateBilling;
